import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Truck, Store, CalendarPlus, XCircle, ChevronDown, ChevronUp, ClipboardCheck, FileDown } from "lucide-react";
import { PriceSummary } from "../PriceSummary";
import { AED } from "../../lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  upcoming: "bg-[#e8f6ff] text-[#0369a1]",
  active: "bg-[#f0f9f0] text-[#009900]",
  completed: "bg-[#f1f3f1] text-[#57685e]",
  cancelled: "bg-red-50 text-red-600",
};

export const BookingCard = ({ b, isOpen, onToggle, onExtend, onCancel }) => {
  const navigate = useNavigate();
  const canManage = b.status !== "cancelled" && b.display_status !== "completed";
  return (
    <div className="bg-white border border-[#e2ebe4] rounded-lg overflow-hidden" data-testid={`booking-card-${b.booking_ref}`}>
      <div className="p-4 flex flex-wrap items-center gap-4">
        <img src={b.vehicle.image} alt={b.vehicle.name} className="w-24 h-16 object-cover rounded" />
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-[#0F1E17]">{b.vehicle.name}</span>
            <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded ${STATUS_STYLES[b.display_status]}`} data-testid="booking-status-badge">
              {b.display_status}
            </span>
            {b.mode === "monthly" && <span className="text-[10px] uppercase tracking-wide bg-[#144733] text-white px-2 py-0.5 rounded">Monthly</span>}
            {b.checkin?.status === "completed" && (
              <span className="text-[10px] uppercase tracking-wide bg-[#FFDC00] text-[#0F1E17] font-semibold px-2 py-0.5 rounded" data-testid="checked-in-badge">
                Checked in · {b.checkin.ra_number}
              </span>
            )}
          </div>
          <div className="text-xs text-[#57685e] mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-[#144733]">{b.booking_ref}</span>
            <span>{dayjs(b.pickup_datetime).format("DD MMM HH:mm")} → {dayjs(b.return_datetime).format("DD MMM HH:mm")}</span>
            <span className="flex items-center gap-1">{b.delivery?.enabled ? <><Truck className="w-3 h-3" /> Delivery</> : <><Store className="w-3 h-3" /> {b.pickup_station}</>}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-[#0F1E17]">{AED(b.quote.total)}</div>
          <div className="text-[10px] text-[#57685e] uppercase">{b.payment.status === "paid" ? "Paid (demo)" : "Pay at counter"}</div>
        </div>
        <button onClick={onToggle} className="text-[#009900]" data-testid="booking-expand-btn">
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-[#e2ebe4] p-4 grid lg:grid-cols-2 gap-4 bg-[#fafcfa]">
          <PriceSummary quote={b.quote} testIdPrefix={`detail-${b.booking_ref}`} />
          <div className="space-y-3">
            {b.delivery?.enabled && (
              <div className="text-xs text-[#57685e] bg-white border border-[#e2ebe4] rounded p-3">
                <strong className="text-[#0F1E17]">Delivery:</strong> {b.delivery.address} · {String(b.delivery.hour).padStart(2, "0")}:00 slot
              </div>
            )}
            <div className="flex gap-3 flex-wrap">
              {b.status !== "cancelled" && (
                <a href={`${API_BASE}/bookings/${b.booking_ref}/invoice.pdf`} target="_blank" rel="noreferrer"
                  className="border-2 border-[#144733] text-[#144733] rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide hover:bg-[#f0f5f0] flex items-center gap-2"
                  data-testid="download-invoice-btn">
                  <FileDown className="w-4 h-4" /> Invoice PDF
                </a>
              )}
              {b.checkin?.status === "completed" && (
                <a href={`${API_BASE}/bookings/${b.booking_ref}/agreement.pdf`} target="_blank" rel="noreferrer"
                  className="border-2 border-[#144733] text-[#144733] rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide hover:bg-[#f0f5f0] flex items-center gap-2"
                  data-testid="download-agreement-btn">
                  <FileDown className="w-4 h-4" /> Rental Agreement PDF
                </a>
              )}
            </div>
            {canManage && b.display_status === "upcoming" && b.checkin?.status !== "completed" && (
              <button onClick={() => navigate(`/checkin/${b.booking_ref}`)}
                className="w-full btn-cta rounded px-4 py-2.5 text-xs flex items-center justify-center gap-2"
                data-testid="online-checkin-btn">
                <ClipboardCheck className="w-4 h-4" /> Check in online
              </button>
            )}
            {canManage && (
              <div className="flex gap-3">
                <button onClick={() => onExtend(b)}
                  className="flex-1 border-2 border-[#009900] text-[#009900] rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide hover:bg-[#f0f9f0] flex items-center justify-center gap-2"
                  data-testid="modify-booking-btn">
                  <CalendarPlus className="w-4 h-4" /> Extend
                </button>
                <button onClick={() => onCancel(b.booking_ref)}
                  className="flex-1 border-2 border-red-500 text-red-600 rounded px-4 py-2.5 text-xs font-medium uppercase tracking-wide hover:bg-red-50 flex items-center justify-center gap-2"
                  data-testid="cancel-booking-btn">
                  <XCircle className="w-4 h-4" /> Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
